import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import { getConsent, hasAnalyticsConsent, setConsentDefaults } from '../utils/consent';
import { API_BASE_URL } from '../utils/constants';
import { useAuth } from '../context/AuthContext';

/**
 * GA4 _ga cookie'sinden client_id'yi oku.
 * Cookie formatı: GA1.2.1234567890.1234567890
 * Client ID, sondan bir önceki segmenttir.
 */
function getGa4ClientId() {
  const match = document.cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/);
  return match ? match[1] : null;
}

/**
 * Backend'e GA4 client_id'yi bildir.
 * Auth token cookie üzerinden otomatik gönderilir (withCredentials: true).
 * Analytics sync asla kritik değil — fire-and-forget.
 */
function syncClientIdToBackend(clientId) {
  if (!clientId) return;
  // ReactSnap prerendering esnasında canlı backend'e istek atma
  if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
  fetch(`${API_BASE_URL}/auth/sync-ga4-client-id`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ client_id: clientId })
  }).catch(() => {
    // Silent fail — analytics sync kritik değil
  });
}

/**
 * Sayfa yolundan content_group belirle.
 * GA4'te sayfaları gruplayarak segmentasyon yapabilmek için.
 */
function getContentGroup(pathname) {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/search/meaning/')) return 'seo_pages';
  if (pathname.startsWith('/search/')) return 'seo_pages';
  if (['/name-meaning', '/name-suggestion', '/name-compare', '/personality', '/harmony', '/analysis', '/baby-name-match'].includes(pathname)) return 'tools';
  if (['/pricing', '/credits', '/checkout'].some(p => pathname.startsWith(p))) return 'ecommerce';
  if (['/login', '/register', '/profile', '/settings', '/forgot-password', '/reset-password'].some(p => pathname.startsWith(p))) return 'account';
  if (pathname.startsWith('/ebooks')) return 'ebooks';
  if (pathname.startsWith('/blog')) return 'blog';
  return 'other';
}

/**
 * Kullanıcının premium durumunu belirle.
 * unlimited_access_until varsa ve gelecekteyse → premium, değilse → free.
 * Kullanıcı giriş yapmamışsa → anonymous.
 */
function getUserType(user) {
  if (!user) return 'anonymous';
  const until = user.unlimited_access_until || user.unlimitedAccessUntil;
  if (until && new Date(until) > new Date()) return 'premium';
  return 'free';
}

export const useGoogleAnalytics = () => {
  const location = useLocation();
  const initializedRef = useRef(false);
  const { user } = useAuth();
  const { i18n } = useTranslation();

  // ── user_id for cross-device / cross-session reporting ──
  // gtag('set', 'user_data', ...) sets a persistent parameter so
  // *every* subsequent event (pageview, web_vitals, custom events)
  // automatically includes user_id.  gtag's command queue is safe
  // to call before initialize() — it replays after the snippet loads.
  useEffect(() => {
    if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;
    if (!hasAnalyticsConsent()) return;

    const userId = user?.id ? String(user.id) : null;
    ReactGA.gtag('set', 'user_data', { user_id: userId });
  }, [user]);

  // ── user_type + language as persistent user properties ──
  // Bunlar user-scoped custom dimension olarak GA4'te tanımlandığında
  // tüm raporlarda segmentasyon yapılabilir hale gelir.
  useEffect(() => {
    if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;
    if (!hasAnalyticsConsent()) return;

    const userType = getUserType(user);
    const language = i18n.language || localStorage.getItem('preferred-language') || 'de';

    ReactGA.gtag('set', 'user_properties', {
      user_type: userType,
      language: language
    });
  }, [user, i18n.language]);

  // ── content_group for page grouping ──
  // Her pageview ile birlikte content_group gönder ki GA4'te
  // sayfa gruplarına göre analiz yapılabilsin.
  useEffect(() => {
    if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;
    if (!initializedRef.current || !hasAnalyticsConsent()) return;

    const contentGroup = getContentGroup(location.pathname);

    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
      title: document.title,
      content_group: contentGroup,
    });
  }, [location]);

  // Listen for consent changes so GA can be re-initialised after the user
  // interacts with the cookie banner.
  useEffect(() => {
    if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;

    const handleConsentChange = () => {
      if (hasAnalyticsConsent()) {
        // User switched from "denied" / no-choice → "granted"
        if (!initializedRef.current) {
          ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID, {
            debug: process.env.NODE_ENV === 'development',
            gtagOptions: { anonymize_ip: true },
          });
          initializedRef.current = true;

          // GA4 _ga cookie'si set edilene kadar bekle, sonra backend'e sync et
          setTimeout(() => {
            syncClientIdToBackend(getGa4ClientId());
          }, 1500);

          // Send a pageview immediately after late-init
          const contentGroup = getContentGroup(location.pathname);
          ReactGA.send({
            hitType: 'pageview',
            page: location.pathname + location.search,
            title: document.title,
            content_group: contentGroup,
          });
        }
      }
    };

    window.addEventListener('cookie-consent-changed', handleConsentChange);

    return () => {
      window.removeEventListener('cookie-consent-changed', handleConsentChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialise on mount ONLY when consent was already granted
  // (e.g. returning user).  For users who have *not* decided yet we wait
  // until handleConsentChange fires (the banner sends a
  // 'cookie-consent-changed' event on first interaction).
  useEffect(() => {
    if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;

    const stored = getConsent();
    if (stored === 'granted') {
      // Consent already given → normal GA init
      ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID, {
        debug: process.env.NODE_ENV === 'development',
        gtagOptions: { anonymize_ip: true },
      });
      initializedRef.current = true;

      // GA4 _ga cookie'si set edilene kadar bekle, sonra backend'e sync et
      setTimeout(() => {
        syncClientIdToBackend(getGa4ClientId());
      }, 1500);

      // İlk pageview'ı burada gönder: init effect, pageview effect'inden SONRA
      // tanımlandığı için mount anında pageview effect initializedRef=false görür.
      ReactGA.send({
        hitType: 'pageview',
        page: location.pathname + location.search,
        title: document.title,
        content_group: getContentGroup(location.pathname),
      });
    } else {
      // No consent yet → push Consent Mode v2 defaults so that Google
      // receives a cookieless ping (modelled data) until the user decides.
      setConsentDefaults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
